import { Checkbox } from '@/components/ui';

export interface CheckboxGroupOption<T extends string> {
  value: T;
  label: string;
}

/**
 * QA-611 체크박스 그룹 선택 규칙(빈 배열 = [전체] = 조회 파라미터 미전송).
 * - 개별 체크 → [전체] 해제 + 그 항목 체크. 개별을 전부 체크하면 [전체]로 바꾼다.
 * - 개별을 모두 해제 → [전체].
 * - [전체] 클릭 → 개별 해제 + [전체](이미 [전체]면 그대로).
 */
export const toggleGroupValue = <T extends string>(
  all: ReadonlyArray<T>,
  current: ReadonlyArray<T>,
  value: T,
  checked: boolean,
): T[] => {
  const next = checked ? [...current.filter((v) => v !== value), value] : current.filter((v) => v !== value);
  if (next.length >= all.length) return [];
  // 선택 순서와 관계없이 항목 순서대로 보낸다.
  return all.filter((v) => next.includes(v));
};

interface Props<T extends string> {
  options: ReadonlyArray<CheckboxGroupOption<T>>;
  value: ReadonlyArray<T>;
  onChange: (next: T[]) => void;
  allLabel: string;
  'aria-label': string;
  'data-testid'?: string;
}

/** 라벨 칸 옆 체크박스 줄 — [전체] 칸 + 항목 칸(고정 너비, 화면이 좁으면 열 수만 줄어 줄바꿈). */
export const CheckboxGroupFilter = <T extends string>({
  options,
  value,
  onChange,
  allLabel,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: Props<T>) => {
  const all = options.map((o) => o.value);
  const isAll = value.length === 0;
  return (
    <div role="group" aria-label={ariaLabel} className="flex min-w-0 items-start" data-testid={testId}>
      <label className="inline-flex w-[132px] shrink-0 items-center gap-1.5 py-1">
        <Checkbox
          checked={isAll}
          onCheckedChange={() => {
            if (!isAll) onChange([]);
          }}
          data-testid={testId ? `${testId}-all` : undefined}
        />
        {allLabel}
      </label>
      <div className="grid min-w-0 flex-1 grid-cols-[repeat(auto-fill,132px)]">
        {options.map((o) => (
          <label key={o.value} className="inline-flex items-center gap-1.5 py-1 pr-2">
            <Checkbox
              checked={value.includes(o.value)}
              onCheckedChange={(v) => onChange(toggleGroupValue(all, value, o.value, v === true))}
              data-testid={testId ? `${testId}-${o.value}` : undefined}
            />
            <span className="truncate" title={o.label}>
              {o.label}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};
